import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Alert, Spinner } from "react-bootstrap";
import { IconPlus, IconEdit, IconCheck } from "@tabler/icons-react";
import { formatPrice } from "utils/utils";
import { useCreateData, useUpdateData } from "hooks/useApiHooks";

const ReservationIncomeForm = ({
  show,
  onHide,
  booking,
  onSuccess,
  apiEndpoint,
}) => {
  const [formData, setFormData] = useState({
    type: "mm",
    payment_bill: "",
    net_agent: "",
    susuk_agent: "",
    others: "",
  });

  const [formula, setFormula] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Determine if this is create or update
  const isCreate = !booking?.reservation_income_id;
  const isUpdate = !!booking?.reservation_income_id;

  // API hooks
  const { createData: createIncome } = useCreateData(
    `/reservation-income-v2`,
    "reservation-income"
  );

  useEffect(() => {
    if (show && booking) {
      if (isUpdate) {
        // For update, populate form with existing data (convert to integer)
        setFormData({
          type: booking.type || "mm",
          payment_bill: booking.payment_bill
            ? parseInt(booking.payment_bill).toString()
            : "",
          net_agent: booking.net_agent
            ? parseInt(booking.net_agent).toString()
            : "",
          susuk_agent: booking.susuk_agent
            ? parseInt(booking.susuk_agent).toString()
            : "",
          others: booking.others ? parseInt(booking.others).toString() : "",
        });
      } else {
        // For create, reset form
        setFormData({
          type: "mm",
          payment_bill: booking.payment_bill
            ? parseInt(booking.payment_bill).toString()
            : "",
          net_agent: booking.net_agent
            ? parseInt(booking.net_agent).toString()
            : "",
          susuk_agent: booking.susuk_agent
            ? parseInt(booking.susuk_agent).toString()
            : "",
          others: booking.others ? parseInt(booking.others).toString() : "",
        });
      }
      setError("");
      setSuccess("");
    }
  }, [show, booking, isUpdate]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFormulaChange = (e) => {
    const value = e.target.value;
    setFormula(value);
    
    // Simple excel-like sum implementation: if starts with =, try to evaluate
    if (value.startsWith("=")) {
      try {
        // Safe evaluation of simple math
        const expression = value.substring(1).replace(/,/g, "");
        // Only allow numbers and operators
        if (/^[0-9+\-*/().\s]+$/.test(expression)) {
          // eslint-disable-next-line no-eval
          const result = eval(expression);
          setFormData(prev => ({
            ...prev,
            net_agent: result.toString()
          }));
        }
      } catch (err) {
        console.error("Formula error", err);
      }
    } else {
      // If not a formula, just use as a manual number if user types it here
      const numValue = value.replace(/[^\d]/g, "");
      if (numValue) {
        setFormData(prev => ({
          ...prev,
          net_agent: numValue
        }));
      }
    }
  };

  // Format number to Rupiah display
  const formatToRupiah = (value) => {
    if (!value) return "";
    const numValue = parseInt(value.toString().replace(/[^\d]/g, "")) || 0;
    return numValue.toLocaleString("id-ID");
  };

  // Parse Rupiah display back to number
  const parseFromRupiah = (value) => {
    if (!value) return "";
    return value.toString().replace(/[^\d]/g, "");
  };

  const handleRupiahInputChange = (field, value) => {
    const rawValue = parseFromRupiah(value);
    setFormData((prev) => ({
      ...prev,
      [field]: rawValue,
    }));
  };

  const handleRupiahFocus = (field) => {
    // When focusing, show raw number without formatting
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field] || "",
    }));
  };

  const handleRupiahBlur = (field) => {
    // When blurring, format for display
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field] || "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.type) {
      setError("Type is required");
      return;
    }

    const submitData = {
      reservation_id: booking.id,
      type: formData.type,
      payment_bill: Number(formData.payment_bill) || 0,
      net_agent: Number(formData.net_agent) || 0,
      susuk_agent: Number(formData.susuk_agent) || 0,
      others: Number(formData.others) || 0,
    };

    try {
      const result = await createIncome(submitData);

      if (result.success) {
        setSuccess(
          isCreate
            ? "Reservation income created successfully!"
            : "Reservation income updated successfully!"
        );
        setError("");

        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess(result.data);
        }

        // Close modal after a short delay
        setTimeout(() => {
          onHide();
        }, 1500);
      } else {
        setError(result.errorResponse.message || "An error occurred");
        setSuccess("");
      }
    } catch (err) {
      setError("An unexpected error occurred");
      setSuccess("");
    }
  };

  const handleClose = () => {
    setFormData({
      type: "mm",
      payment_bill: "",
      net_agent: "",
      susuk_agent: "",
      others: "",
    });
    setError("");
    setSuccess("");
    onHide();
  };

  const calculateNetKuber = () => {
    const paymentBill = Number(formData.payment_bill) || 0;
    const netAgent = Number(formData.net_agent) || 0;
    const susukAgent = Number(formData.susuk_agent) || 0;
    const others = Number(formData.others) || 0;

    return paymentBill - netAgent - susukAgent - others;
  };

  if (!booking) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isCreate ? (
            <>
              <IconPlus size={20} className="me-2" />
              Create Reservation Income
            </>
          ) : (
            <>
              <IconEdit size={20} className="me-2" />
              Update Reservation Income
            </>
          )}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {booking && (
            <div className="mb-4 p-3 bg-light rounded">
              <h6 className="mb-2">Booking Details</h6>
              <Row>
                <Col md={6}>
                  <small className="text-muted">Code:</small>
                  <div className="fw-bold">{booking.code}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Date:</small>
                  <div>{booking.reservation_date}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Guest:</small>
                  <div>{booking.guest_name}</div>
                </Col>
                <Col md={6}>
                  <small className="text-muted">Payment Method:</small>
                  <div>{booking.payment_method_name}</div>
                </Col>
              </Row>
              {booking.last_updated && (
                <div className="mt-2">
                  <small className="text-muted">Last Updated:</small>
                  <div className="text-primary">
                    {new Date(booking.last_updated).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error and Success Messages */}
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" className="mb-3">
              <IconCheck size={16} className="me-2" />
              {success}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Type *</Form.Label>
                <Form.Select
                  value={formData.type}
                  onChange={(e) => handleInputChange("type", e.target.value)}
                  required
                >
                  <option value="nam">NAM</option>
                  <option value="mm">MM</option>
                  <option value="bm">BM</option>
                  <option value="josh">JOSH</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Payment Bill (Rp)</Form.Label>
                <Form.Control
                  type="text"
                  value={formatToRupiah(formData.payment_bill)}
                  onChange={(e) =>
                    handleRupiahInputChange("payment_bill", e.target.value)
                  }
                  onFocus={() => handleRupiahFocus("payment_bill")}
                  onBlur={() => handleRupiahBlur("payment_bill")}
                  placeholder="0"
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="text-primary fw-bold">Formula Rumus (Excel-like Sum for Net Agent)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. =100000+50000"
                  value={formula}
                  onChange={handleFormulaChange}
                  className="border-primary"
                />
                <Form.Text className="text-muted">
                  Ketik rumus diawali dengan "=" (contoh: =200000+150000) untuk menjumlahkan otomatis ke Net Agent.
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Net Agent (Rp)</Form.Label>
                <Form.Control
                  type="text"
                  value={formatToRupiah(formData.net_agent)}
                  onChange={(e) =>
                    handleRupiahInputChange("net_agent", e.target.value)
                  }
                  onFocus={() => handleRupiahFocus("net_agent")}
                  onBlur={() => handleRupiahBlur("net_agent")}
                  placeholder="0"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Others (Rp)</Form.Label>
                <Form.Control
                  type="text"
                  value={formatToRupiah(formData.others)}
                  onChange={(e) =>
                    handleRupiahInputChange("others", e.target.value)
                  }
                  onFocus={() => handleRupiahFocus("others")}
                  onBlur={() => handleRupiahBlur("others")}
                  placeholder="0"
                />
              </Form.Group>
            </Col>
            {/* <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Susuk Agent (Rp)</Form.Label>
                <Form.Control
                  type="text"
                  value={formatToRupiah(formData.susuk_agent)}
                  onChange={(e) =>
                    handleRupiahInputChange("susuk_agent", e.target.value)
                  }
                  onFocus={() => handleRupiahFocus("susuk_agent")}
                  onBlur={() => handleRupiahBlur("susuk_agent")}
                  placeholder="0"
                />
              </Form.Group>
            </Col> */}
          </Row>

          <Row>
            
            {/* <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Net Kuber (Calculated)</Form.Label>
                <div
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "#e9ecef",
                    border: "1px solid #ced4da",
                    borderRadius: "4px",
                    fontWeight: "500",
                    color: calculateNetKuber() >= 0 ? "#28a745" : "#dc3545",
                  }}
                >
                  {formatPrice(calculateNetKuber())}
                </div>
              </Form.Group>
            </Col> */}
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!!success}>
            {isCreate ? "Create" : "Update"} Reservation Income
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ReservationIncomeForm;
